<template>
  <div>
    <div id="imageEditor" style="width: 100%; height: 100vh"></div>
    <TConfirmationModal
      ref="filenameModalRef"
      :disabled="disabledConfirm"
      close-out-no
      success
    >
      <template #header>{{ $t("file_manager.confirm_file_name") }}</template>
      <div class="w-[500px]">
        <ChooseFolder
          :links="links"
          :actual-path="actualPath"
          :folders="folders"
          @choose-folder="loadFiles"
        />

        <TInput
          v-model="filename"
          data-testid="pexels-image-name"
          :label="$t('file_manager.file_name')"
          :error-input="disabledConfirm"
          :error-text="$t('file_manager.file_name_exists', { msg: filename })"
          @update:model-value="checkName"
          @on-focus="
            $event.target.setSelectionRange(0, $event.target.value.indexOf('.'))
          "
        />
      </div>
      <template #confirm-action>
        {{ $t("layout.save") }}
      </template>
    </TConfirmationModal>
  </div>
</template>

<script lang="ts" setup>
  import { useFileManagerStore } from "@/stores/fileManger";
  // import { useApiKeysStore } from "@/stores/apiKeyStore";
  import { useImageEditorStore } from "@/stores/imageEditorStore";
  import { useGeneralStore } from "@/stores/generalStore";
  import { useNotificationsStore } from "@/stores/notifications";
  import { useFontStore } from "@/stores/fonts";
  import { IImage } from "@/schemas/image/imageSchema";

  import { TConfirmationModal, TInput } from "@topol/twig-ui";
  import { ChooseFolder, useFile, imagePickerBus } from "@topol/image-picker";

  import { useI18n } from "vue-i18n";
  import { v4 as uuidv4 } from "uuid";
  import { ref, Ref, onMounted, onUnmounted, computed } from "vue";

  const url = ref("");
  const filename = ref("");
  const filenameModalRef = ref();
  const fileItems: Ref<IImage[]> = ref([]);
  const disabledConfirm = ref(false);
  const imgEditor = ref();
  const isSaving = ref(false);
  const folders: Ref<Array<IImage[]>> = ref([]);

  const { t } = useI18n();

  const fileManagerStore = useFileManagerStore();
  // const apiKeysStore = useApiKeysStore();
  const imageEditorStore = useImageEditorStore();
  const generalStore = useGeneralStore();
  const notificationStore = useNotificationsStore();
  const fontStore = useFontStore();

  const { actualPath, _uploadFile, loadFoldersFromApi } = useFile(
    fileManagerStore.FMOptions
  );

  const links = computed(() => {
    const paths = actualPath.value.split("/");
    let absolutePath = "/";
    const links = [
      {
        name: t("file_manager.root"),
        path: "/",
      },
    ];
    for (const path of paths) {
      if (path != "") {
        absolutePath += path + "/";
        links.push({
          name: path,
          path: absolutePath,
        });
      }
    }
    return links;
  });

  const defaultPath = computed(() => {
    return fileManagerStore.defaultPath || "/";
  });

  function loadScript() {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://d3ayqk5orocicw.cloudfront.net/dist/tim.js";
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  function loadEditor() {
    loadScript()
      .then(() => {
        // @ts-expect-error
        if (typeof Tim !== "undefined") {
          load();
        } else {
          notificationStore.createNotification({
            type: "error",
            text: "Error loading image editor",
          });
          close();
        }
      })
      .catch((err) => {
        console.error("Error loading scripts:", err);
        notificationStore.createNotification({
          type: "error",
          text: "Error loading image editor",
        });
        close();
      });
  }

  function load() {
    url.value = imageEditorStore.photoEditorImage as string;
    // @ts-expect-error
    imgEditor.value = Tim({
      config: {
        authorize: {
          apiKey: "test",
          entityId: "test",
        },
        hideTopbarControls: ["download"],
        light: generalStore.light ? true : false,
        language: generalStore.language,
        aiFunctions: imageEditorStore.AIinEditor,
        enableStartWithChoice: imageEditorStore.createNew,
        startWithBlankChoice: imageEditorStore.createNew,
        export: {
          formats: ["jpeg", "png"],
          defaultFormat: "jpeg",
          pngOnlyIfTransparent: true,
        },
        customFonts: fontStore.customFonts,
        hideEditorControls: imageEditorStore.options.hideControls,
      },
      onInit() {
        if (!imageEditorStore.createNew) {
          // @ts-expect-error
          ImageEditor.loadFileFromUrl(url.value);
        }
      },
      onSave(blob: string) {
        onSaveAndClose(blob);
      },
    });
    imgEditor.value.render("#imageEditor");
    // @ts-expect-error
    imgEditor.value.event.on(zoid.EVENT.CLOSE, () => {
      if (isSaving.value) {
        return;
      }
      close();
    });
  }

  function close() {
    imageEditorStore.photoEditorImage = null;
    imageEditorStore.createNew = false;
  }

  function checkName() {
    const found = fileItems.value.find(
      (f) =>
        f.name === filename.value && (f.new === undefined || f.new === false)
    );

    if (found !== undefined) {
      disabledConfirm.value = true;
      return;
    } else {
      disabledConfirm.value = false;
    }
  }

  async function onSaveAndClose(base64: string) {
    isSaving.value = true;
    let extension = "";

    if (base64.startsWith("data:image/jpeg")) {
      extension = ".jpg";
    } else {
      extension = ".png";
    }

    if (imageEditorStore.createNew) {
      filename.value = "new_image_" + uuidv4() + extension;
    } else {
      filename.value = decodeURI(url.value).substring(
        url.value.lastIndexOf("/") + 1
      );
      filename.value = filename.value.substring(
        0,
        filename.value.lastIndexOf(".")
      );

      if (filename.value.includes("_edited_")) {
        filename.value = filename.value.substring(
          0,
          filename.value.indexOf("_edited_")
        );
      }

      filename.value = filename.value + "_edited_" + uuidv4() + extension;

      if (filename.value.length > 1024) {
        filename.value = "edited_" + uuidv4() + extension;
      }
    }

    imgEditor.value.close();
    const filenameModal = await filenameModalRef.value.open();

    if (filenameModal === "cancel") {
      isSaving.value = false;
      close();
      return;
    }

    const contentType = base64.substring(
      base64.indexOf(":") + 1,
      base64.indexOf(";")
    );
    const base64data = base64.split(",").slice(1).join(",");
    const file = base64toBlob(base64data, contentType);
    try {
      const response = await _uploadFile(
        file,
        actualPath.value,
        filename.value
      );

      if (response) {
        fileManagerStore.defaultPath = actualPath.value;
        window.$event.emit(
          "chooseFileFromImageEditor",
          JSON.parse(response.xhr.response).url
        );
        imagePickerBus.emit("imageCreated", actualPath.value);
        close();
      } else {
        notificationStore.createNotification({
          title: "Error while saving image",
          type: "error",
          text: "",
        });
        close();
      }
    } catch (error) {
      notificationStore.createNotification({
        title: "Error while saving image",
        type: "error",
        text: "",
      });
      close();
    }

    isSaving.value = false;
  }

  async function loadFiles(path: string, index?: number, force = false) {
    const loadedFolders = await loadFoldersFromApi(path, force);

    if (index !== undefined) {
      folders.value.splice(index, folders.value.length - index);
    }

    if (loadedFolders) {
      folders.value.push(
        loadedFolders.filter((item) => item.type === "folder")
      );
      fileItems.value = loadedFolders.filter((item) => item.type === "file");
      checkName();
    }
  }

  function base64toBlob(b64Data: string, contentType = "", sliceSize = 512) {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    return new Blob(byteArrays, { type: contentType });
  }
  onMounted(async () => {
    const paths = defaultPath.value.split("/");
    let absolutePath = "/";
    await loadFiles(absolutePath);
    for (const path of paths) {
      if (path != "") {
        absolutePath += path + "/";
        await loadFiles(absolutePath);
      }
    }
    actualPath.value = defaultPath.value;
    // @ts-expect-error
    if (typeof Tim !== "undefined") {
      load();
    } else {
      loadEditor();
    }
  });

  onUnmounted(() => {
    if (imgEditor.value) {
      imgEditor.value.close();
    }
    close();
  });
</script>

<style></style>
